import React, { useState, useEffect, useRef } from 'react';
import styles from './Modal.module.css';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Dashboard from '../Dashboard/Dashboard';
import { cleanupVideoElement } from '../../utils/videoCleanup'; // ✅ Import cleanup utility

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerateVideo: () => void;
  downloadLinkRef: React.MutableRefObject<string>;
  projectIdRef: React.MutableRefObject<string>;
  isProcessing: boolean;
  showDashboard: boolean;
  jobId: string | null;
  onCompletionCallback: (videoUrl: string, projectId: string) => void;
}

const VideoModal: React.FC<VideoModalProps> = ({ 
  isOpen, 
  onClose, 
  onGenerateVideo, 
  downloadLinkRef,
  projectIdRef,
  isProcessing,
  showDashboard,
  jobId,
  onCompletionCallback
}) => {
  const navigate = useNavigate();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRefModal = useRef<HTMLVideoElement>(null); // ✅ Add ref for video element
  
  // Get video URL from either the ref or generatedVideoUrl prop
  const videoUrl = downloadLinkRef.current;
  const hasVideo = videoUrl && videoUrl.trim() !== '';

  // Proceed to caption page
  const handleProceedToCaptioning = () => {
    console.log('Proceeding to captioning with video URL:', videoUrl);
    console.log('Project ID:', projectIdRef.current);
    
    if (hasVideo && projectIdRef.current) {
      // Close the modal first
      onClose();
      
      // Use setTimeout to ensure modal closes before navigation
      setTimeout(() => {
        const navigationUrl = `/caption?video-url=${encodeURIComponent(videoUrl)}&project-id=${projectIdRef.current}`;
        console.log('Navigating to:', navigationUrl);
        // Use window.location.href to force page reload
        window.location.href = navigationUrl;
      }, 100);
    } else {
      console.error('Missing required data:', {
        hasVideo,
        videoUrl,
        projectId: projectIdRef.current
      });
    }
  };

  // Reset loaded state when modal changes
  useEffect(() => {
    if (!isOpen) {
      setVideoLoaded(false);
      // ✅ Cleanup video element when modal closes
      if (videoRefModal.current) {
        cleanupVideoElement(videoRefModal.current);
      }
    }
  }, [isOpen]);

  // ✅ Cleanup on unmount
  useEffect(() => {
    return () => {
      if (videoRefModal.current) {
        cleanupVideoElement(videoRefModal.current);
      }
    };
  }, []);

  return (
    <div className={`${styles.modalOverlay} ${isOpen ? styles.open : ''}`} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose} aria-label="Close Modal">
          <X size={24} />
        </button>
        <h2 className={styles.modalTitle}>{hasVideo ? 'Caption Video' : 'Generate Video'}</h2>
        
        <div className={styles.modalBody}>
          {/* Show video preview if available */}
          {hasVideo && (
            <div className={styles.videoPreview}>
              <video
                ref={videoRefModal}
                src={videoUrl}
                controls
                onLoadedData={() => setVideoLoaded(true)}
                className={styles.video}
              >
                Your browser does not support HTML video.
              </video>
              {!videoLoaded && <p style={{ color: '#ccc', fontSize: '12px' }}>Loading video preview...</p>}
            </div>
          )}
          
          {/* Show Dashboard during processing */}
          {showDashboard && jobId && (
            <div className={styles.dashboardContainer}>
              <Dashboard 
                jobId={jobId}
                callBackFunc={onCompletionCallback}
                displayOption="compact"
              />
            </div>
          )}
          
          {/* Guidance text based on video availability */}
          <p className={styles.prompt}>
            {hasVideo 
              ? "Your video is ready for captioning. Click the button below to proceed to the captioning page."
              : "No generated video is available. Generate a video before proceeding to captioning."
            }
          </p>
        </div>
        
        <div className={styles.modalFooter}>
          {hasVideo ? (
            <button
              className={styles.button}
              onClick={handleProceedToCaptioning}
            >
              Proceed to Captioning 
            </button>
          ) : (
            <button
              className={styles.button}
              onClick={onGenerateVideo}
              disabled={isProcessing}
            >
              {isProcessing ? 'Generating...' : 'Generate Video'}
            </button>
          )}
          <button className={styles.buttonSecondary} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
